import { compareMoney, parseMoney, toMoneyNumber } from '@erp/shared-utils';
import type {
  AgingBucketDefinition,
  ArAgingCustomerRow,
  ArAgingTotals,
  ArCustomerProfileResponse,
} from '@erp/rpc';
import type { ReportDataset } from '@/components/report-canvas/ReportDataContext';

// ── AR dataset adapters ───────────────────────────────────────────────────────
// Pure functions shaping AR aggregates into the report canvas's tabular
// dataset form. Money decimal strings are converted to numbers here — charts
// need numeric values for axis domains. `t` translates user-facing labels
// (pass useGT()'s translator; identity in tests).

type Translate = (message: string) => string;

export const AR_DATASET_KEYS = {
  agingBuckets: 'ar-aging-buckets',
  topCustomers: 'ar-top-customers',
  recentPayments: 'ar-recent-payments',
} as const;

function money(value: string | null | undefined): number {
  return toMoneyNumber(parseMoney(value ?? '0'));
}

/** One row per aging bucket with the org/customer total in that bucket. */
export function agingBucketsDataset(
  totals: ArAgingTotals | null,
  bucketDefinitions: AgingBucketDefinition[],
  t: Translate,
  isLoading?: boolean,
): ReportDataset {
  return {
    key: AR_DATASET_KEYS.agingBuckets,
    label: t('Aging buckets'),
    columns: [
      { key: 'bucket', label: t('Aging Bucket'), dataType: 'string' },
      { key: 'amount', label: t('Amount'), dataType: 'number' },
    ],
    rows:
      totals === null
        ? []
        : bucketDefinitions.map((def, i) => ({
            bucket: def.label,
            amount: money(totals.bucketAmounts[i]),
          })),
    isLoading,
  };
}

/** Top customers by total outstanding, one row per customer. */
export function topCustomersDataset(
  customers: ArAgingCustomerRow[],
  t: Translate,
  isLoading?: boolean,
  limit = 15,
): ReportDataset {
  const rows = [...customers]
    .sort((a, b) => compareMoney(b.totalOutstanding, a.totalOutstanding))
    .slice(0, limit)
    .map((c) => ({
      customer: c.customerName ?? t('(Unknown)'),
      totalDue: money(c.totalOutstanding),
    }));
  return {
    key: AR_DATASET_KEYS.topCustomers,
    label: t('Top customers by balance'),
    columns: [
      { key: 'customer', label: t('Customer'), dataType: 'string' },
      { key: 'totalDue', label: t('Total Due'), dataType: 'number' },
    ],
    rows,
    isLoading,
  };
}

/** The customer's recent payments (customer profile surface only). */
export function recentPaymentsDataset(
  profile: ArCustomerProfileResponse | null,
  t: Translate,
  isLoading?: boolean,
): ReportDataset {
  return {
    key: AR_DATASET_KEYS.recentPayments,
    label: t('Recent payments'),
    columns: [
      { key: 'paymentDate', label: t('Payment Date'), dataType: 'string' },
      { key: 'amount', label: t('Amount'), dataType: 'number' },
    ],
    rows: (profile?.recentPayments ?? []).map((p) => ({
      paymentDate: p.paymentDate,
      amount: money(p.amount),
    })),
    isLoading,
  };
}
